// Pestaña «Panel de control» de la ficha: arriba, lo que hay por hacer y qué le pasa al equipo;
// abajo, el estado, la situación del proveedor y las acciones agrupadas por situación del cliente.
import { type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { MdmDeviceStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmDeviceStatusEnum';
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import { cn } from '@/lib/cn';
import { Button, HelpTooltip } from '@/kit';
import { daysBetweenMx, formatDateMx, formatDateTimeMx } from '@/lib/dateHelpers';
import {
    dayCountLabel,
    deviceHardwareLabel,
    explainMissingEnrollmentDate,
    isTerminalStatus,
    MDM_HEARTBEAT_MEANING,
    MDM_LOCK_MODE_SHORT_LABEL,
    MDM_OPERATION_LABEL,
    MDM_PROVIDER_LABEL,
    MDM_PROVIDER_STATUS_LABEL,
    MISSING_LOCK_MODE_NOTE,
    NO_VALUE,
    operationCountLabel,
    readDeviceSituation,
    readHeartbeat,
    readLockPhase,
    lockFellAt,
    lockPhaseLabel,
    CUTOFF_REFUTED_NOTE,
    LOCK_REFUTED_NOTE,
    STALE_OPERATION_MS,
} from '../logic';
import {
    IN_FLIGHT_REASON,
    isActionHiddenByPermission,
    resolveActionEligibility,
} from '../actionEligibility';
import {
    findAction,
    getMdmActionGroups,
    type MdmAction,
    type MdmActionGroupKey,
    type MdmActionKey,
} from '../mdmActions';
import { type MdmAbilities } from '../permissions';
import { observedAgoLabel } from '../refreshResultLines';
import type { MdmDeviceSituation, MdmFleetDevice, MdmRefreshReview, MdmStatusTone } from '../types';
import { hasLockRefutedAlert, readDeviceAlerts, type MdmAlertLine } from '../deviceAlerts';
import { BreakableCode } from './BreakableCode';
import { CARD_TITLE, InfoStrip, Metric, PanelCard, SUB_LINE } from './MdmPanelUi';
import { MdmRefreshResultPanel } from './MdmRefreshResultPanel';
import { MdmStatusPill } from './MdmStatusPill';

/** La consulta al proveedor tal como la ficha la opera: el botón del encabezado y su resultado. */
export interface MdmRefreshControl {
    /** Lanza la consulta del equipo abierto. No abre diálogo: leer no se confirma. */
    onRefresh: () => void;
    isPending: boolean;
    /** El veredicto de la última consulta de esta sesión; `null` mientras no haya ninguna. */
    lastReview: MdmRefreshReview | null;
    /** El equipo como estaba al apretar; de ahí sale el «antes» de cada fila. */
    before: MdmFleetDevice | null;
}

export interface MdmControlPanelTabProps {
    device: MdmFleetDevice;
    abilities: MdmAbilities;
    /** Instante de referencia, fijo mientras la pantalla está abierta. */
    now: Date | string;
    refresh: MdmRefreshControl;
    onAction: (action: MdmAction) => void;
}

const LOCK_REASON_LABEL: Record<LoanDeviceLockReasonEnum, string> = {
    [LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1]: 'Cuota vencida (T+1)',
    [LoanDeviceLockReasonEnum.ADMIN_MANUAL]: 'Decisión de un administrador',
    [LoanDeviceLockReasonEnum.FRAUD]: 'Fraude',
};

/**
 * Observaciones del proveedor que concuerdan con cada estado nuestro. Lo que no está aquí es
 * divergencia y se le enseña al operador; el resto es la misma realidad con otro vocabulario.
 */
const EXPECTED_PROVIDER_STATUS: Record<LoanDeviceMdmStatusEnum, readonly MdmDeviceStatusEnum[]> = {
    [LoanDeviceMdmStatusEnum.NOT_ENROLLED]: [MdmDeviceStatusEnum.NOT_ENROLLED],
    [LoanDeviceMdmStatusEnum.ENROLLING]: [
        MdmDeviceStatusEnum.ENROLLMENT_PENDING,
        MdmDeviceStatusEnum.NOT_ENROLLED,
    ],
    [LoanDeviceMdmStatusEnum.ACTIVE]: [MdmDeviceStatusEnum.ACTIVE, MdmDeviceStatusEnum.NOTIFIED],
    [LoanDeviceMdmStatusEnum.LOCKED]: [MdmDeviceStatusEnum.LOCKED],
    [LoanDeviceMdmStatusEnum.RELEASED]: [MdmDeviceStatusEnum.RELEASED],
    [LoanDeviceMdmStatusEnum.ARCHIVED]: [
        MdmDeviceStatusEnum.NOT_ENROLLED,
        MdmDeviceStatusEnum.RELEASED,
    ],
    [LoanDeviceMdmStatusEnum.FAILED]: [
        MdmDeviceStatusEnum.UNKNOWN,
        MdmDeviceStatusEnum.NOT_ENROLLED,
    ],
    [LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND]: [
        MdmDeviceStatusEnum.UNKNOWN,
        MdmDeviceStatusEnum.NOT_ENROLLED,
    ],
    [LoanDeviceMdmStatusEnum.MANUAL_REVIEW]: [
        MdmDeviceStatusEnum.UNKNOWN,
        MdmDeviceStatusEnum.ENROLLMENT_PENDING,
    ],
};

const ALERT_TONE_CLASS: Record<MdmAlertLine['tone'], string> = {
    danger: 'border-destructive/40 bg-destructive/10 text-destructive',
    warn: 'border-warning-soft-border bg-warning-soft text-warning',
};

/** Lo que este equipo necesita de una persona, lo más urgente primero. Sin alertas no se monta. */
function AlertsList({ alerts }: { alerts: readonly MdmAlertLine[] }) {
    return (
        <section aria-labelledby="mdm-alerts-title" className="space-y-1.5">
            <h3 id="mdm-alerts-title" className={cn(CARD_TITLE, 'text-foreground')}>
                Por hacer
            </h3>
            <ul className="space-y-1.5">
                {alerts.map((alert, index) => (
                    <li
                        key={`${alert.code}-${String(index)}`}
                        className={cn(
                            'flex items-start gap-1.5 rounded-md border px-3 py-2 text-[11.5px] font-semibold leading-relaxed',
                            ALERT_TONE_CLASS[alert.tone],
                        )}
                    >
                        <AlertTriangle className="mt-[3px] size-3.5 shrink-0" aria-hidden />
                        <span className="min-w-0 flex-1">{alert.text}</span>
                        {alert.help ? (
                            <HelpTooltip content={alert.help} label="Ayuda: esta alerta" />
                        ) : null}
                    </li>
                ))}
            </ul>
        </section>
    );
}

const SITUATION_TONE_CLASS: Record<MdmStatusTone, string> = {
    success: 'border-success-soft-border bg-success-soft',
    danger: 'border-destructive/40 bg-destructive/10',
    warning: 'border-warning-soft-border bg-warning-soft',
    info: 'border-info/40 bg-info/10',
    neutral: 'border-border bg-muted/40',
};

/**
 * Lo primero que el manager lee: qué le pasa al equipo y por qué. El titular se pinta siempre; la
 * línea «Conviene X» solo cuando hay una acción que sugerir.
 */
function SituationHeader({
    situation,
    suggestedLabel,
}: {
    situation: MdmDeviceSituation;
    /** La acción sugerida, si la hay. Sin ella el encabezado igual se pinta. */
    suggestedLabel?: string;
}) {
    return (
        <section
            className={cn('rounded-lg border px-3 py-2.5', SITUATION_TONE_CLASS[situation.tone])}
        >
            <h3 className={cn(CARD_TITLE, 'text-foreground')}>Qué le pasa a este equipo</h3>
            <p className="mt-1 text-[12.5px] font-semibold leading-snug text-foreground">
                {situation.headline}
            </p>
            <p className="mt-1.5 text-[11.5px] leading-relaxed text-foreground/80">
                {suggestedLabel ? (
                    <strong className="font-bold">
                        {`Conviene ${suggestedLabel.toLowerCase()}. `}
                    </strong>
                ) : null}
                {situation.reason}
            </p>
        </section>
    );
}

/**
 * Cómo cierra cada proveedor su ciclo asíncrono. Ninguno manda webhooks, y es lo que cambia lo
 * que el operador hace con una operación pendiente.
 */
const PROVIDER_PENDING_NOTE: Record<MdmProviderEnum, string> = {
    [MdmProviderEnum.DATACULTR]:
        'Datacultr no envía notificaciones del proveedor: la pendiente la cierra el sondeo automático, no esta pantalla. Actualizar te dice cómo ve el proveedor al equipo ahora mismo.',
    [MdmProviderEnum.TRUSTONIC]:
        'Trustonic tampoco envía notificaciones del proveedor: la pendiente la cierra el sondeo automático, no esta pantalla. Actualizar te dice cómo ve el proveedor al equipo ahora mismo.',
    [MdmProviderEnum.MOTOSAFE]:
        'Motosafe tampoco envía notificaciones del proveedor: la pendiente la cierra el sondeo automático, no esta pantalla. Actualizar te dice cómo ve el proveedor al equipo ahora mismo.',
};

/** Qué es el folio del proveedor, para el `?` de la card de diagnóstico. */
const POLLING_HANDLE_NOTE =
    'El folio lo emite el proveedor y es con lo que el sistema le pregunta por esa operación; desde aquí solo sirve para trazabilidad de soporte.';

/** El hueco que el expediente deja con ese proveedor. Se declara en vez de suponerlo lleno. */
const PROVIDER_UNKNOWN_NOTE: Partial<Record<MdmProviderEnum, string>> = {
    [MdmProviderEnum.TRUSTONIC]:
        'El expediente no trae la modalidad del servicio ni su fecha de caducidad. Si este equipo es de prepago, Trustonic lo bloquea solo al vencer esa fecha, y desde aquí no hay forma de saber cuál es.',
};

/**
 * La nota del grupo del PIN; el largo del código es el que declara el proveedor del equipo. Solo
 * la lee Trustonic: en Datacultr el botón nunca se habilita, así que el grupo ni se pinta.
 */
function pinGroupNote(device: MdmFleetDevice): string {
    const code =
        device.devicePasskeyLength === undefined
            ? 'el código'
            : `el código de ${String(device.devicePasskeyLength)} dígitos`;
    return `El PIN lo emite el proveedor y esta pantalla es el único lugar donde se muestra: no queda en la bitácora. Pídele al cliente ${code} que ve en la pantalla de su equipo bloqueado.`;
}

/** Lo que hay que saber ANTES de apretar, solo en los grupos donde cambia lo que el operador hace. */
const GROUP_NOTE: Partial<Record<MdmActionGroupKey, string>> = {
    creditEnded:
        'Liberar del MDM NO es desbloquear: saca el equipo del MDM para siempre y ya no se podrá volver a bloquear. Úsalo solo en créditos liquidados, F5 confirmados o ventas de contado posteriores al otorgamiento.',
};

/** La nota del grupo, que en el del PIN depende del equipo. */
function getGroupNote(groupKey: MdmActionGroupKey, device: MdmFleetDevice): string | undefined {
    return groupKey === 'customerCantUse' ? pinGroupNote(device) : GROUP_NOTE[groupKey];
}

const KIND_CLASS: Partial<Record<MdmAction['kind'], string>> = {
    destructive: 'border-destructive/40 text-destructive hover:bg-destructive/10',
    sensitive: 'border-warning/40 text-warning hover:bg-warning/10',
};

function ActionButton({
    action,
    label,
    description,
    suggested,
    warning,
    onAction,
}: {
    action: MdmAction;
    /** La etiqueta a pintar; en la consulta de estado depende del proveedor del equipo. */
    label: string;
    /** El porqué de la acción. Vive en el `?`, no bajo el botón. */
    description: string;
    suggested?: boolean;
    /** Lo que el operador debe saber antes de apretar; va escrito bajo el botón. */
    warning?: string;
    onAction: (action: MdmAction) => void;
}) {
    const noteId = `mdm-action-${action.key}-note`;
    return (
        <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-start gap-1">
                <button
                    type="button"
                    // El nombre accesible es SOLO la etiqueta: sin esto arrastra el badge.
                    aria-label={label}
                    aria-describedby={warning ? noteId : undefined}
                    onClick={() => {
                        onAction(action);
                    }}
                    className={cn(
                        'flex h-full min-w-0 flex-1 flex-col items-start gap-0.5 rounded-md border bg-background px-2.5 py-2 text-left transition-colors',
                        'hover:bg-accent',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        KIND_CLASS[action.kind] ?? 'border-border text-foreground',
                        suggested && 'border-primary/60 ring-1 ring-primary/40',
                    )}
                >
                    <span className="flex flex-wrap items-center gap-1.5 text-[11.5px] font-bold">
                        {label}
                        {suggested ? (
                            <span className="rounded-full bg-primary/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-primary">
                                sugerido
                            </span>
                        ) : null}
                    </span>
                </button>
                <HelpTooltip content={description} label={`Ayuda: ${label}`} className="mt-2" />
            </div>
            {warning ? (
                <p id={noteId} className="text-[10.5px] leading-snug text-warning">
                    {warning}
                </p>
            ) : null}
        </div>
    );
}

function ActionsGrid({ children }: { children: ReactNode }) {
    return (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-2">{children}</div>
    );
}

/**
 * El botón de la consulta y qué tan vieja es la foto que se está leyendo. Vive junto a la píldora
 * de estado porque es lo que refresca ese estado, no una acción más de la reja.
 */
function RefreshHeaderControl({
    action,
    refresh,
    device,
    now,
    disabledReason,
}: {
    action: MdmAction;
    refresh: MdmRefreshControl;
    device: MdmFleetDevice;
    now: Date | string;
    /** Por qué no se puede consultar; el botón se apaga con el motivo escrito, no se esconde. */
    disabledReason?: string;
}) {
    // La consulta de esta sesión manda sobre la última escritura del expediente: es más fresca.
    const observedAt = refresh.lastReview?.observedAt ?? device.updatedAt;
    const noteId = 'mdm-refresh-note';
    return (
        <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabledReason !== undefined}
                    aria-describedby={disabledReason ? noteId : undefined}
                    isLoading={refresh.isPending}
                    leftIcon={<RefreshCw className="size-3.5" aria-hidden />}
                    onClick={refresh.onRefresh}
                >
                    {action.label}
                </Button>
                {observedAt ? (
                    <span className="text-[10.5px] text-muted-foreground">
                        Actualizado {observedAgoLabel(observedAt, now)}
                    </span>
                ) : null}
            </div>
            {disabledReason ? (
                <p id={noteId} className="text-[10.5px] leading-snug text-warning">
                    {disabledReason}
                </p>
            ) : null}
        </div>
    );
}

/** La consulta no cambia el equipo: siempre se ofrece y no cuenta como acción disponible. */
const QUERY_ACTION_KEYS: readonly MdmActionKey[] = ['refresh'];

/** Lo que se dice cuando, fuera de las consultas, no hay nada que hacer con el equipo. */
export const NO_ACTIONS_AVAILABLE = 'Este equipo no tiene acciones disponibles ahora.';

/** Reja de métricas: `auto-fit` reparte por el ancho del PANEL, no por el de la ventana. */
const METRIC_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-3';

/** La misma idea con celdas más anchas: las referencias del proveedor son cadenas largas. */
const REFERENCE_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-3';

/**
 * `Bloqueado desde <hora del corte>` si el corte ya cayó; si solo se conoce la orden, `Orden de
 * bloqueo: <fecha>`, porque el candado cae después de la orden.
 */
function lockedSinceLine(device: MdmFleetDevice, now: Date | string): string | null {
    if (device.mdmStatus !== LoanDeviceMdmStatusEnum.LOCKED) return null;
    const reason = device.lockReason
        ? LOCK_REASON_LABEL[device.lockReason]
        : 'motivo sin registrar';
    const phase = readLockPhase(device, now);
    if (phase && phase !== 'locked') {
        return `${lockPhaseLabel(phase, device.currentLockScheduledFor)} · ${reason}`;
    }
    const fellAt = lockFellAt(device, now);
    if (fellAt) return `Bloqueado desde ${formatDateTimeMx(fellAt)} · ${reason}`;
    if (!device.lockedAt) return null;
    return `Orden de bloqueo: ${formatDateTimeMx(device.lockedAt)} · ${reason}`;
}

/**
 * El corte se muestra SIEMPRE que exista: en Datacultr sobrevive a un desbloqueo. Vencido, habla en
 * pasado, y un candado confirmado ya lo cuenta «Bloqueado desde».
 */
function scheduledLockLine(device: MdmFleetDevice, now: Date | string): string | null {
    const cutAt = device.currentLockScheduledFor;
    if (!cutAt) return null;
    const when = formatDateTimeMx(cutAt);
    if (new Date(cutAt).getTime() > new Date(now).getTime()) return `Se autobloquea el ${when}`;
    const phase = readLockPhase(device, now);
    if (phase === 'locked') return null;
    if (phase === 'cutoffPassed') {
        return `El corte estaba programado para el ${when}; el proveedor no confirma si el equipo quedó bloqueado.`;
    }
    if (phase === 'cutoffRefuted') {
        return `El corte estaba programado para el ${when}; el proveedor dice que el equipo sigue sin candado.`;
    }
    return device.mdmConfirmed
        ? `El corte estaba programado para el ${when}.`
        : `El corte estaba programado para el ${when}; el proveedor no lo ha confirmado.`;
}

/**
 * Modo programado, equipo activo y sin fecha de corte: la reprogramación no se instaló y nadie lo
 * va a volver a bloquear. Es lo que queda cuando el toast del momento ya se cerró.
 */
function missingScheduledLockLine(device: MdmFleetDevice): string | null {
    if (device.mdmLockMode !== MdmLockModeEnum.SCHEDULED) return null;
    if (device.mdmStatus !== LoanDeviceMdmStatusEnum.ACTIVE) return null;
    if (device.currentLockScheduledFor) return null;
    return 'El equipo no tiene bloqueo automático programado. Nadie lo va a volver a bloquear solo: revisa la bitácora y escálalo.';
}

/**
 * De dónde sale el modo en cada proveedor. En Trustonic y Datacultr es el de NUESTRO expediente y
 * puede diferir del del proveedor; Motosafe solo admite bajo demanda.
 */
const LOCK_MODE_NOTE: Record<MdmProviderEnum, string> = {
    [MdmProviderEnum.TRUSTONIC]:
        'Se congeló al enrolar el equipo y decide qué puede hacer este teléfono. Cambiarlo obliga a darlo de baja y volver a enrolarlo. Es el modo de nuestro expediente: el proveedor todavía no lo expone, así que puede diferir del suyo.',
    [MdmProviderEnum.DATACULTR]:
        'Sale del producto que se financió y define cuándo entra el candado. Es el modo de nuestro expediente: el proveedor todavía no lo expone, así que puede diferir del suyo.',
    [MdmProviderEnum.MOTOSAFE]:
        'Motosafe solo bloquea al momento, así que sus equipos siempre van bajo demanda: el candado entra cuando el sistema manda la orden.',
};

function readLockModeNote(device: MdmFleetDevice): string {
    return device.mdmLockMode ? LOCK_MODE_NOTE[device.mdmProvider] : MISSING_LOCK_MODE_NOTE;
}

const DATACULTR_PIN_NOTE =
    'Datacultr no entrega el código de desbloqueo sin conexión: su API responde que no hay pass key disponible. Con este proveedor el PIN no está disponible.';

/**
 * Por qué a un Datacultr bloqueado no le sale el botón del PIN. Solo con el candado puesto, que es
 * cuando el operador lo busca; sin candado la nota no viene al caso.
 */
function datacultrPinLine(device: MdmFleetDevice): string | null {
    if (device.mdmProvider !== MdmProviderEnum.DATACULTR) return null;
    if (device.mdmStatus !== LoanDeviceMdmStatusEnum.LOCKED) return null;
    return DATACULTR_PIN_NOTE;
}

/**
 * La observación del proveedor solo se enseña cuando contradice al expediente, que es el que manda.
 * Sale del veredicto de la consulta: sin consulta no hay observación y no se afirma contradicción.
 */
function providerDriftLine(device: MdmFleetDevice, review: MdmRefreshReview | null): string | null {
    const observed = review?.providerStatus;
    if (!observed || isTerminalStatus(device.mdmStatus)) return null;
    if (EXPECTED_PROVIDER_STATUS[device.mdmStatus].includes(observed)) return null;
    const label = MDM_PROVIDER_STATUS_LABEL[observed];
    return `El proveedor lo reporta ${label} (consultado el ${formatDateTimeMx(review.observedAt)})`;
}

interface OperationSituation {
    tone: 'warn' | 'danger' | 'success';
    headline: string;
    /** El dato que acompaña al titular; la operación en vuelo no lleva, su porqué va en el `?`. */
    detail?: string;
}

/** Los 3 estados de la card del proveedor: en vuelo, estancada o sin pendientes. */
function readOperationSituation(device: MdmFleetDevice, now: Date | string): OperationSituation {
    const { pendingOperations, lastOperationAt } = device;
    if (pendingOperations === 0) {
        return {
            tone: 'success',
            headline: 'Sin operaciones pendientes.',
            detail: lastOperationAt
                ? `Última operación: ${lastOperationLabel(device)} · ${formatDateTimeMx(lastOperationAt)}.`
                : 'Este equipo todavía no tiene ninguna llamada al proveedor.',
        };
    }
    if (!lastOperationAt) {
        return {
            tone: 'danger',
            headline: `${operationCountLabel(pendingOperations)} sin fecha de última llamada.`,
            detail: 'No hay cómo saber desde cuándo espera: revisa la bitácora del equipo.',
        };
    }
    const elapsedMs = new Date(now).getTime() - new Date(lastOperationAt).getTime();
    if (elapsedMs < STALE_OPERATION_MS) {
        return {
            tone: 'warn',
            headline: `${operationCountLabel(pendingOperations)} en vuelo desde el ${formatDateTimeMx(lastOperationAt)}.`,
        };
    }
    const days = daysBetweenMx(lastOperationAt, now);
    return {
        tone: 'danger',
        headline: `${operationCountLabel(pendingOperations)} ${pendingOperations === 1 ? 'estancada' : 'estancadas'} desde el ${formatDateTimeMx(lastOperationAt)}.`,
        detail: `Lleva ${dayCountLabel(days)} sin cerrarse. Consulta el estado y considera reintentar.`,
    };
}

/** Sin pendientes, la referencia es la de la última operación: llamarla «pendiente» contradice la card. */
function operationReferenceLabel(device: Pick<MdmFleetDevice, 'pendingOperations'>): string {
    return device.pendingOperations > 0 ? 'Operación pendiente' : 'Última referencia';
}

/** El nombre en español de la última llamada; sin tipo registrado se lo llama por lo que es. */
function lastOperationLabel(device: MdmFleetDevice): string {
    return device.lastOperationType
        ? MDM_OPERATION_LABEL[device.lastOperationType]
        : 'Operación sin tipo registrado';
}

export function MdmControlPanelTab({
    device,
    abilities,
    now,
    refresh,
    onAction,
}: MdmControlPanelTabProps) {
    const heartbeat = readHeartbeat(device.lastSeenAt, now);
    const situation = readDeviceSituation(device, now);
    const alerts = readDeviceAlerts(device, now);
    const suggestedKey = situation.suggested;
    const isDatacultr = device.mdmProvider === MdmProviderEnum.DATACULTR;
    const providerUnknownNote = PROVIDER_UNKNOWN_NOTE[device.mdmProvider];
    const lockedSince = lockedSinceLine(device, now);
    const lockPhase = readLockPhase(device, now);
    const scheduledLock = scheduledLockLine(device, now);
    const missingScheduledLock = missingScheduledLockLine(device);
    const providerDrift = providerDriftLine(device, refresh.lastReview);
    const datacultrPin = datacultrPinLine(device);
    const operationSituation = readOperationSituation(device, now);
    const missingEnrollmentHint = explainMissingEnrollmentDate(device.mdmStatus);
    const lockModeNote = readLockModeNote(device);

    /** La advertencia de la acción si se pinta; `null` si no aplica o el rol no la tiene sembrada. */
    const readAvailability = (action: MdmAction): { warning?: string } | null => {
        if (isActionHiddenByPermission(action.key, abilities)) return null;
        const eligibility = resolveActionEligibility(action, device, abilities, now);
        return eligibility.enabled ? { warning: eligibility.warning } : null;
    };

    // Solo se pinta la acción que aplica; un grupo sin ninguna tampoco se pinta.
    const actionGroups = getMdmActionGroups()
        .map((group) => ({
            group,
            actions: group.actions.flatMap((action) => {
                const available = readAvailability(action);
                return available ? [{ action, warning: available.warning }] : [];
            }),
        }))
        .filter(({ actions }) => actions.length > 0);

    const hasAvailableAction = actionGroups.some(({ actions }) =>
        actions.some(({ action }) => !QUERY_ACTION_KEYS.includes(action.key)),
    );
    const noActionsLine = hasAvailableAction
        ? null
        : device.pendingOperations > 0
          ? IN_FLIGHT_REASON
          : NO_ACTIONS_AVAILABLE;

    // La consulta no entra en los grupos: su elegibilidad se resuelve aparte, para el encabezado.
    const refreshAction = findAction('refresh');
    const refreshEligibility = refreshAction
        ? resolveActionEligibility(refreshAction, device, abilities, now)
        : undefined;

    // El encabezado solo propone una acción que la tabla de elegibilidad dé por habilitada.
    const suggestedAction = suggestedKey === null ? undefined : findAction(suggestedKey);
    const suggestedLabel =
        suggestedAction && readAvailability(suggestedAction) ? suggestedAction.label : undefined;

    return (
        <div className="space-y-3">
            {alerts.length > 0 ? <AlertsList alerts={alerts} /> : null}

            <SituationHeader situation={situation} suggestedLabel={suggestedLabel} />

            <PanelCard
                title="Estado actual · Operación día a día"
                meta={deviceHardwareLabel(device)}
            >
                <div>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <MdmStatusPill
                            status={device.mdmStatus}
                            size="md"
                            lockPhase={lockPhase}
                            mdmConfirmed={device.mdmConfirmed}
                            lockScheduledFor={device.currentLockScheduledFor}
                        />
                        {refreshAction ? (
                            <RefreshHeaderControl
                                action={refreshAction}
                                refresh={refresh}
                                device={device}
                                now={now}
                                disabledReason={refreshEligibility?.reason}
                            />
                        ) : null}
                    </div>
                    {lockedSince ? (
                        <div className={cn(SUB_LINE, 'text-muted-foreground')}>{lockedSince}</div>
                    ) : null}
                    {scheduledLock ? (
                        <div className={cn(SUB_LINE, 'text-muted-foreground')}>{scheduledLock}</div>
                    ) : null}
                    {providerDrift ? (
                        <div className={cn(SUB_LINE, 'text-warning')}>{providerDrift}</div>
                    ) : null}
                    {datacultrPin ? (
                        <div className={cn(SUB_LINE, 'text-muted-foreground')}>{datacultrPin}</div>
                    ) : null}
                </div>

                <div className={cn(METRIC_GRID, 'border-t border-border pt-3')}>
                    <Metric
                        label="Señal del equipo"
                        value={heartbeat.longLabel}
                        help={MDM_HEARTBEAT_MEANING[device.mdmProvider]}
                        tone={
                            heartbeat.severity === 'warn'
                                ? 'warn'
                                : heartbeat.severity === 'danger'
                                  ? 'danger'
                                  : heartbeat.severity === 'ok'
                                    ? 'ok'
                                    : 'neutral'
                        }
                    />
                    <Metric
                        label="Ops pendientes"
                        value={device.pendingOperations}
                        tone={device.pendingOperations > 0 ? 'warn' : 'ok'}
                    />
                    <Metric
                        label="Último error"
                        value={
                            device.lastError ? (
                                <BreakableCode value={device.lastError.code} />
                            ) : (
                                'ninguno'
                            )
                        }
                        wrap
                        tone={device.lastError ? 'danger' : 'ok'}
                    />
                    <Metric
                        label="Enrolado desde"
                        value={device.enrolledAt ? formatDateMx(device.enrolledAt) : NO_VALUE}
                        help={device.enrolledAt ? undefined : missingEnrollmentHint}
                    />
                    <Metric
                        label="Modo de bloqueo"
                        value={
                            device.mdmLockMode
                                ? MDM_LOCK_MODE_SHORT_LABEL[device.mdmLockMode]
                                : NO_VALUE
                        }
                        help={lockModeNote}
                    />
                    {/* Con el corte vencido, esa confirmación es anterior al corte y ya no respalda «Activo». */}
                    {device.mdmConfirmed &&
                    device.mdmConfirmedAt &&
                    lockPhase !== 'cutoffPassed' ? (
                        <Metric
                            label="Confirmado"
                            value={formatDateTimeMx(device.mdmConfirmedAt)}
                            help={`Lo confirmamos el ${formatDateTimeMx(device.mdmConfirmedAt)}. Es cuándo nos enteramos, no la hora en que el candado cayó en el equipo.`}
                        />
                    ) : null}
                </div>

                {lockPhase === 'refuted' && !hasLockRefutedAlert(device) ? (
                    <InfoStrip tone="danger">
                        <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
                        <strong className="font-bold">Bloqueo desmentido.</strong>{' '}
                        {LOCK_REFUTED_NOTE}
                    </InfoStrip>
                ) : null}

                {lockPhase === 'cutoffRefuted' && !hasLockRefutedAlert(device) ? (
                    <InfoStrip tone="danger">
                        <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
                        <strong className="font-bold">Corte desmentido.</strong>{' '}
                        {CUTOFF_REFUTED_NOTE}
                    </InfoStrip>
                ) : null}

                {missingScheduledLock ? (
                    <InfoStrip tone="danger">
                        <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
                        <strong className="font-bold">Sin bloqueo automático.</strong>{' '}
                        {missingScheduledLock}
                    </InfoStrip>
                ) : null}

                {device.lastError ? (
                    <InfoStrip tone="danger">
                        <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
                        <strong className="font-bold">Último error:</strong>{' '}
                        {device.lastError.message ?? device.lastError.code}{' '}
                        {device.lastError.at === undefined ? null : (
                            <span className="opacity-70">
                                ({formatDateTimeMx(device.lastError.at)})
                            </span>
                        )}
                    </InfoStrip>
                ) : null}
            </PanelCard>

            {/* Sin fila previa el «antes» es el equipo de ahora: ninguna fila afirma un cambio que no hubo. */}
            {refresh.lastReview ? (
                <MdmRefreshResultPanel
                    review={refresh.lastReview}
                    before={refresh.before ?? device}
                    after={device}
                    now={now}
                />
            ) : null}

            {isDatacultr ? (
                <PanelCard
                    title="Situación Datacultr · notificaciones y pendientes"
                    meta="diagnóstico"
                    help={PROVIDER_PENDING_NOTE[device.mdmProvider]}
                >
                    <div className={REFERENCE_GRID}>
                        <Metric
                            label={operationReferenceLabel(device)}
                            value={device.operationReference ?? NO_VALUE}
                            mono
                        />
                        <Metric
                            label="Última operación"
                            value={
                                device.lastOperationAt
                                    ? `${lastOperationLabel(device)} · ${formatDateTimeMx(device.lastOperationAt)}`
                                    : 'sin llamadas'
                            }
                            wrap
                        />
                    </div>
                    <InfoStrip tone={operationSituation.tone}>
                        <strong className="font-bold">{operationSituation.headline}</strong>
                        {operationSituation.detail ? ` ${operationSituation.detail}` : null}
                    </InfoStrip>
                </PanelCard>
            ) : (
                <PanelCard
                    title={`Situación ${MDM_PROVIDER_LABEL[device.mdmProvider]} · operaciones pendientes`}
                    meta="diagnóstico"
                    help={[
                        PROVIDER_PENDING_NOTE[device.mdmProvider],
                        device.pollingHandle ? POLLING_HANDLE_NOTE : null,
                        providerUnknownNote,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    {/* Sin folio ni referencia no hay nada que trazar: dos guiones no le dicen nada al operador. */}
                    {(device.operationReference ?? device.pollingHandle) ? (
                        <div className={REFERENCE_GRID}>
                            <Metric
                                label={operationReferenceLabel(device)}
                                value={device.operationReference ?? NO_VALUE}
                                mono
                            />
                            <Metric
                                label="Folio del proveedor"
                                value={device.pollingHandle ?? NO_VALUE}
                                mono
                            />
                        </div>
                    ) : null}
                    <InfoStrip tone={operationSituation.tone}>
                        <strong className="font-bold">{operationSituation.headline}</strong>
                        {operationSituation.detail ? ` ${operationSituation.detail}` : null}
                    </InfoStrip>
                </PanelCard>
            )}

            {noActionsLine ? (
                <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-center text-[11.5px] italic text-muted-foreground">
                    {noActionsLine}
                </p>
            ) : null}

            {actionGroups.map(({ group, actions }) => (
                <PanelCard
                    key={group.key}
                    title={group.title}
                    danger={group.key === 'creditEnded'}
                    help={getGroupNote(group.key, device)}
                >
                    <ActionsGrid>
                        {actions.map(({ action, warning }) => {
                            const copy = action;
                            return (
                                <ActionButton
                                    key={action.key}
                                    action={action}
                                    label={copy.label}
                                    description={copy.description}
                                    suggested={suggestedKey === action.key}
                                    warning={warning}
                                    onAction={onAction}
                                />
                            );
                        })}
                    </ActionsGrid>
                </PanelCard>
            ))}
        </div>
    );
}
